<template>
  <div class="w-full h-full overflow-hidden relative">
    <div
      v-if="!hasValidConfig"
      class="absolute inset-0 flex items-center justify-center"
      :style="{ color: themeVars.text2, backgroundColor: themeVars.primary6 }"
    >
      <div class="flex flex-col items-center space-y-2">
        <CIcon :color="themeVars.text2" icon="file-image" size="48" />
        <span class="text-sm">{{ t('canvasUI.CSlideViewer.noPdfAvailable') }}</span>
      </div>
    </div>
    <div
      v-else-if="error"
      class="absolute inset-0 flex items-center justify-center"
      :style="{ color: themeVars.error }"
    >
      {{ t('canvasUI.CSlideViewer.error') }}: {{ error }}
    </div>
    <iframe
      v-else-if="iframeUrl"
      ref="iframeRef"
      class="border-0"
      sandbox="allow-scripts allow-same-origin"
      :src="iframeUrl"
      :style="containerStyle"
      @load="onIframeLoad"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
const { t } = useI18n()
const themeVars = useThemeVars()

const PITCHER_COM_PSPDFKIT_LICENSE_KEY =
  'rKD0Ja3L6Oc5svmubXZreNFSpA9VvLzEsEyseyTteXwtfdUpHnvvwixbr3kbdQvLIRV6M03ZI1nWuwRRQjhBnTOgsy5uvwYXAmJGzHcXDSMAxhF7kHpiEwHDnfHDA-QBpuKtOh-9EP9HBFYPX4tDLAUMypErrtKQSkW4eTiW5yxWtkrrJR4Y1-LXtUnVMaCV_LLVFSdpJUCPH6r7U1rEP_d2eKE3zLHhYg9tm4SwE2bzfMGj7jTb74e-KcQ4oULa-Jd4oHKlQdgADw_KznBaj2ONBNo97BMVsWQqLMqHV9bsZIMBn4qYPMr7Oc60msTBz5XT47-KCRXIEixl56SvniHoVj5Ni9Qqz4WyoAnBOLuQL_O-GnxrMxWGew15YFFXxFtZLg4ZDWm6viuy3KbQE7X7q04dDvz2OsBGuPCcA1lVQp_oDvWP34898gEEZuB7-PoIOiQFDgaryv7nL5BbAza5kKvTqeTkeR6QuFbdSoKqhurkdsQ03kvDU0sgJEW4-D8hZSYa7iDcCoStmpf2SpnKmL7fO_F4f0IlirwJQerm6nfK3aiiF5FqGnStJ_v2dLFnxPl65r7RjmhzeOVOi9-zn3cy1HAjWV35k1dfwYsQBuEHjW6fhCK5GtAc3QBIQONqr3XhhzaUEO_ytOxSGy5gSpK28bi3HD8CCzmRcOeJtprDPAdygmnKD9t8mM68'
const LOCALHOST_PSPDFKIT_LICENSE_KEY =
  'UOP39K_A0yxEkWC8OtuOVZ3nGZbcYsvA7SeOUZwe-fylY4y9Xl-yJDu2YHlUeNtEca9R5EOvY8yrkg3VMSNJC4j8tOePEHAFII2I8LpLP6UtRZ4yeDdc2gw1Cu-H46qAwnKnW0b6ohQ2dCOMPOjb8YnAiEnxREkypw39YmFJL1i14zRPfzwmnk0cJZKFrpOKSVx_tLEtFeXKcg9Mh2vsYHYFpyis4g9zqxQwOrMywDgvoWbg2-hZmIKPSKqd8xUybxAHd59g6fDcJyop_ksxR0cuTms_XHrgN44JjeXU_gfoiLIMQCq6XE32L92SuG5nR26jKE8msScvfZJDZsSeFRxBx6oF0AxkMNZySZ-Yja88ZflgP2U9U7weXzl64LMv5cv16uXTL1myMsS2b4_MU2y4EOMLcxSJYkyhDDpBtCdo_ojUtvWN_ySvjEP7CJ8nwH-2d9F1CTazBmyfGl9BYWWRAxX3AakJxSqoQzJxeVlPvGlFEYXcytko9iyGGbKkik0zNov-wLqVzUFmHXxwVTQWCTq1uLiT8SxO5cfpTn4j8CgSlFSVANZJsNP0Fec4s3VsvzFPWSxn93ta801FRsUSy_vb6uvz4j5nU6ZvSluY4s0u7q8Nuhr5ay7moYmpTcdfJEeh9Q5YzngJgCXrI_qkRfI7F5ZVUxJoPegsqlvEIIkrPNIWa1xDM5AYjHXS'

const props = withDefaults(defineProps<{ pageIndex?: number; pdfUrl: string }>(), {
  pageIndex: 0,
})

const emit = defineEmits<{ pdfLoaded: [] }>()

const error = ref<string | null>(null)
const iframeRef = ref<HTMLIFrameElement | null>(null)
const isReady = ref(false)
const iframeUrl = ref<string | null>(null)

const hasValidConfig = computed(() => !!props.pdfUrl && props.pdfUrl.trim() !== '')

const containerStyle = computed(() => {
  return {
    width: '100%',
    height: '100%',
    position: 'absolute' as const,
    top: '0',
    left: '0',
    opacity: isReady.value ? 1 : 0.001,
    transition: 'none',
  }
})

function getDocumentUrl(): string {
  return import.meta.env.DEV
    ? props.pdfUrl?.replace(/https:\/\/[^./]+\.my\.pitcher\.com/g, 'http://localhost:3000') || ''
    : props.pdfUrl || ''
}

function getIframeHtml(): string {
  const bgColor = themeVars.value.base || '#ffffff'
  const licenseKey = import.meta.env.DEV ? LOCALHOST_PSPDFKIT_LICENSE_KEY : PITCHER_COM_PSPDFKIT_LICENSE_KEY
  const pdfUrl = getDocumentUrl()
  const themeVarsJson = JSON.stringify(themeVars.value)

  /* eslint-disable no-useless-escape */
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: ${bgColor};
    }
    #pspdfkit-container {
      width: 100%;
      height: 100%;
      background: ${bgColor};
    }
  </style>
</head>
<body>
  <div id="pspdfkit-container"></div>
  <script src="https://cdn.cloud.pspdfkit.com/pspdfkit-web@1.7.0/nutrient-viewer.js"><\/script>
  <script>
    (async () => {
      try {
        const response = await fetch('${pdfUrl}', { credentials: 'include' });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const instance = await PSPDFKit.load({
          container: '#pspdfkit-container',
          document: blobUrl,
          licenseKey: '${licenseKey}',
          autoSaveMode: PSPDFKit.AutoSaveMode.DISABLED,
          initialViewState: new PSPDFKit.ViewState({
            scrollMode: PSPDFKit.ScrollMode.PER_SPREAD,
            layoutMode: PSPDFKit.LayoutMode.SINGLE,
            viewportPadding: {
              vertical: 0,
              horizontal: 0,
            },
            pageSpacing: 0,
            spreadSpacing: 0,
            currentPageIndex: ${props.pageIndex ?? 0},
            readOnly: true,
            showToolbar: false,
            showAnnotations: false,
            showComments: false,
            showAnnotationNotes: false,
            enableAnnotationToolbar: false,
            zoom: PSPDFKit.ZoomMode.FIT_TO_WIDTH,
            prerenderedPageSpreads: 0,
          }),
          theme: 'LIGHT',
          enableRichText: () => false,
          enableHistory: false,
          disableTextSelection: false,
          disableForms: true,
          renderPagePreview: false,
          disableIndexedDBCaching: true,
          enableAutomaticLinkExtraction: false,
          enableClipboardActions: false,
          standaloneInstancesPoolSize: 0,
          isEditableAnnotation: () => false,
          isEditableComment: () => false,
        });

        // Inject styles into PSPDFKit shadow DOM
        function injectStyles() {
          const pspdfkitContainer = document.querySelector('.PSPDFKit-Container');
          if (!pspdfkitContainer?.shadowRoot) return false;

          const rootEl = pspdfkitContainer.shadowRoot.querySelector('.PSPDFKit-Root');
          if (!rootEl) return false;

          // Inject theme vars
          const themeVars = ${themeVarsJson};
          Object.entries(themeVars).forEach(([key, value]) => {
            rootEl.style.setProperty('--' + key, value);
          });

          // Inject styles into shadow root
          const shadowRoot = rootEl.getRootNode();
          const style = document.createElement('style');
          style.textContent = \`.PSPDFKit-Container, .PSPDFKit-Scroll { overflow: hidden !important; background: ${bgColor} !important; } .PSPDFKit-Root { background: ${bgColor} !important; touch-action: none !important; user-select: none !important; } .PSPDFKit-Stage, .PSPDFKit-Document { background: ${bgColor} !important; } .PSPDFKit-Spread, .PSPDFKit-SpreadStack, .PSPDFKit-Page-Container { margin: 0 !important; padding: 0 !important; background: ${bgColor} !important; box-shadow: none !important; border: none !important; } .PSPDFKit-Page { box-shadow: none !important; border: 0 !important; background: transparent !important; margin: 0 !important; padding: 0 !important; } .PSPDFKit-Spread-Background, .PSPDFKit-Page-Background { background: ${bgColor} !important; } .PSPDFKit-Viewport::-webkit-scrollbar, .PSPDFKit-Scroll::-webkit-scrollbar { display: none !important; } .PSPDFKit-NavigationButton, .PSPDFKit-PageIndicator, .PSPDFKit-Toolbar, .PSPDFKit-Sidebar { display: none !important; } .PSPDFKit-Viewport { background: ${bgColor} !important; overflow: hidden !important; touch-action: none !important; } :host { background: ${bgColor} !important; }\`;
          shadowRoot.appendChild(style);

          return true;
        }

        // Wait for shadow DOM to be ready
        let attempts = 0;
        const injectInterval = setInterval(() => {
          if (injectStyles() || attempts++ > 20) {
            clearInterval(injectInterval);
          }
        }, 50);

        document.addEventListener('keydown', (e) => {
          const navKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
          if (navKeys.includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
          }
        }, { capture: true });

        document.addEventListener('wheel', (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, { passive: false, capture: true });

        document.addEventListener('touchmove', (e) => {
          e.preventDefault();
          e.stopPropagation();
        }, { passive: false, capture: true });

        window.parent.postMessage({ type: 'pspdfkit-loaded' }, '*');
      } catch (err) {
        console.error('PSPDFKit load error:', err);
        window.parent.postMessage({ type: 'pspdfkit-error', error: err.message }, '*');
      }
    })();
  <\/script>
<\/body>
<\/html>`
}
/* eslint-enable */

function onIframeLoad() {
  // iframe content will postMessage when ready
}

function handleMessage(event: MessageEvent) {
  if (event.data?.type === 'pspdfkit-loaded') {
    setTimeout(() => {
      isReady.value = true
      emit('pdfLoaded')
    }, 1000)
  } else if (event.data?.type === 'pspdfkit-error') {
    error.value = event.data.error || 'Failed to load PDF'
  }
}

onMounted(() => {
  window.addEventListener('message', handleMessage)
  const html = getIframeHtml()
  const blob = new Blob([html], { type: 'text/html' })
  iframeUrl.value = URL.createObjectURL(blob)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage)
  if (iframeUrl.value) {
    URL.revokeObjectURL(iframeUrl.value)
  }
  isReady.value = false
})
</script>
